import { useMemo } from 'react';
import i18next from 'i18next';
import { aggregateByCoalition } from '../../utils/coalitionLookup';

const VIEWBOX_W = 720;
const VIEWBOX_H = 380;
const DOT_R = 5;
const RING_SPACING = DOT_R * 2.4;

// Build concentric semicircular rings sized to fit `total` seats.
// Returns positions sorted left-to-right (angle π → 0).
const hemicyclePositions = (total) => {
  let R = 1;
  let rings = [];
  while (R <= 60) {
    rings = [];
    let cap = 0;
    for (let r = 0; r < R; r++) {
      // r = 0 is innermost; outermost ring has the largest radius
      const radius = (r + 1) * RING_SPACING + RING_SPACING * 0.6;
      const slots = Math.max(1, Math.floor((Math.PI * radius) / RING_SPACING));
      rings.push({ radius, slots, seats: 0 });
      cap += slots;
    }
    if (cap >= total) break;
    R++;
  }

  const totalSlots = rings.reduce((s, x) => s + x.slots, 0);
  let assigned = 0;
  rings.forEach((ring, idx) => {
    if (idx === rings.length - 1) {
      ring.seats = total - assigned;
    } else {
      ring.seats = Math.min(ring.slots, Math.round((ring.slots / totalSlots) * total));
      assigned += ring.seats;
    }
  });

  const positions = [];
  rings.forEach((ring) => {
    const { radius, seats } = ring;
    if (seats <= 0) return;
    for (let i = 0; i < seats; i++) {
      const t = seats === 1 ? 0.5 : i / (seats - 1);
      const angle = Math.PI - t * Math.PI;
      positions.push({
        x: radius * Math.cos(angle),
        y: -radius * Math.sin(angle),
        angle,
        radius,
      });
    }
  });

  positions.sort((a, b) => {
    if (Math.abs(a.angle - b.angle) > 1e-6) return b.angle - a.angle; // left → right
    return a.radius - b.radius;
  });
  return positions;
};

const SeatsMap = ({ election }) => {
  const groups = useMemo(() => aggregateByCoalition(election), [election]);

  const total = election.totalSeats || groups.reduce((a, b) => a + b.seats, 0);

  const positions = useMemo(() => hemicyclePositions(total), [total]);

  // Order coalitions on the arc: biggest opposition on left, winning coalition on right.
  const winning = groups.find(
    (g) =>
      g.coalition === election.winningCoalition ||
      (election.winningCoalition || '').includes(g.coalition),
  );
  const opposition = groups.filter((g) => g !== winning);
  const ordered = [...opposition, ...(winning ? [winning] : [])];

  // Build sequence of {color, coalition, party} per seat.
  const seatSeq = [];
  ordered.forEach((g) => {
    if (g.parties.length > 0) {
      g.parties.forEach((p) => {
        for (let i = 0; i < p.seats; i++) {
          seatSeq.push({ color: g.color, coalition: g.coalition, party: p.party });
        }
      });
    } else {
      for (let i = 0; i < g.seats; i++) {
        seatSeq.push({ color: g.color, coalition: g.coalition, party: g.coalition });
      }
    }
  });
  // In case of any sum mismatch (data quirks for early years), pad with neutral.
  while (seatSeq.length < positions.length) {
    seatSeq.push({ color: '#cccccc', coalition: 'Unknown', party: '—' });
  }

  // Compute viewBox transform: place arc baseline near the bottom.
  const cx = VIEWBOX_W / 2;
  const cy = VIEWBOX_H - 40;
  // Auto-fit horizontally based on outermost ring radius.
  const maxR = Math.max(...positions.map((p) => p.radius), RING_SPACING);
  const scale = Math.min((VIEWBOX_W - 40) / (maxR * 2), (VIEWBOX_H - 60) / maxR);

  return (
    <div className="seatsmap">
      <svg
        className="seatsmap__svg"
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        role="img"
        aria-label="Parliament hemicycle"
      >
        <g transform={`translate(${cx}, ${cy})`}>
          {/* baseline */}
          <line
            x1={-maxR * scale - 6}
            y1={2}
            x2={maxR * scale + 6}
            y2={2}
            stroke="#d4d4d8"
            strokeWidth="1"
          />
          {positions.map((p, i) => {
            const seat = seatSeq[i];
            return (
              <circle
                key={i}
                cx={p.x * scale}
                cy={p.y * scale}
                r={DOT_R}
                fill={seat.color}
                stroke="#fff"
                strokeWidth="0.7"
              >
                <title>{`${seat.coalition} · ${seat.party}`}</title>
              </circle>
            );
          })}
          {/* center label: total seats */}
          <text
            x={0}
            y={-RING_SPACING * 0.2}
            textAnchor="middle"
            fontSize="22"
            fontWeight="700"
            fill="#1a1a1a"
          >
            {total}
          </text>
          <text
            x={0}
            y={RING_SPACING * 1.0}
            textAnchor="middle"
            fontSize="11"
            fill="#8a8a8a"
          >
            {i18next.t('总议席')}
          </text>
        </g>
      </svg>

      <div className="seatsmap__legend">
        {ordered.map((g) => (
          <div key={g.coalition} className="seatsmap__legend-item">
            <span className="seatsmap__legend-dot" style={{ backgroundColor: g.color }} />
            <span className="seatsmap__legend-name">
              {g.coalition}
              {g === winning && (
                <span className="seatsmap__legend-badge">{i18next.t('执政')}</span>
              )}
            </span>
            <span className="seatsmap__legend-count">{g.seats}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SeatsMap;
